// Alias to avoid a name clash with the TinyMCE editor type below.
import { Editor as TinyMCEReactEditor } from "@tinymce/tinymce-react";
import React, { useEffect, useRef } from "react";
import type { RawEditorOptions } from "tinymce";

import "tinymce/tinymce"; // registers the global `tinymce` instance
import "tinymce/plugins/lists";
import "tinymce/plugins/link";
import "tinymce/plugins/autoresize";
import "tinymce/plugins/code";
import "tinymce/themes/silver/theme";
import "tinymce/skins/ui/oxide/skin.min.css";

const DEFAULT_COLOR_MAP = [
  // Format: [hex, label]
  "#000000",
  "Black",
  "#FFFFFF",
  "White",
  "#FF0000",
  "Red",
  "#00FF00",
  "Green",
  "#0000FF",
  "Blue",
  "#F1C40F",
  "Yellow",
  "#E67E22",
  "Orange",
  "#9B59B6",
  "Purple",
  "#182b49",
  "Highlight Color",
];

const DEFAULT_TOOLBAR =
  "bold italic underline | forecolor backcolor | align | bullist numlist | link | removeformat | code";
const DEFAULT_PLUGINS = ["lists", "link", "autoresize", "code"];
const DEFAULT_VALID_ELEMENTS =
  "p[class|style],a[href|target|style],strong/b,em/i,span[class|style],br,ul,ol,li";
const DEFAULT_CONTENT_STYLE = `
  body { font-family:Helvetica,Arial,sans-serif; font-size:14px; background-color: #ffffff; }
  body.mce-content-readonly { background-color: #d1d1d1; }
  a { text-decoration: none !important; }
`;

// TinyMCE's HTML parser force-lowercases the *value* of any `color`/`background-color`
// declaration it parses (tinymce/tinymce.js: "Lowercase colors like RED"). That's normally
// harmless, but our mail templates put case-sensitive placeholders like `{{ V3_HIGHLIGHT_COLOR }}`
// there, which get mangled to `{{ v3_highlight_color }}`. This isn't a one-time thing on
// paste/insert -- `editor.getContent()` re-parses the live DOM through the same schema parser
// on every call, and it's called on every keystroke (via onEditorChange), so the property name
// has to stay disguised for the entire time TinyMCE holds the content, not just around insertion.
// So: rename the property before it ever reaches TinyMCE's parser (`BeforeSetContent`, covering
// every path that inserts content -- the `value` prop, paste, the code-view dialog's Save) and
// rename it back on every `editor.getContent()` call (`GetContent`, covering onEditorChange and
// the code-view dialog reading current content to display it), rather than just once at some
// single point -- there isn't one path all content leaves through.
const COLOR_STYLE_GUARD_PREFIX = "tmpguard-";

const guardColorPlaceholders = (html: string): string =>
  html.replace(/style\s*=\s*(['"])([\s\S]*?)\1/gi, (_match, quote, styleBody) => {
    const guarded = styleBody.replace(
      /\b(background-color|color)(\s*:)/gi,
      (_m: string, prop: string, colon: string) => `${COLOR_STYLE_GUARD_PREFIX}${prop}${colon}`,
    );
    return `style=${quote}${guarded}${quote}`;
  });

const unguardColorPlaceholders = (styleAttr: string): string =>
  styleAttr.replace(new RegExp(`${COLOR_STYLE_GUARD_PREFIX}(background-color|color)`, "gi"), "$1");

export type TinyEditorProps = {
  value: string;
  onChange: (content: string) => void;
  placeholder?: string;
  disabled?: boolean;

  /** Starting height in px. Grows up to `maxHeight` while the `autoresize` plugin is active. */
  height?: number;
  minHeight?: number;
  maxHeight?: number;

  /** Toolbar spec, TinyMCE's own string syntax (groups separated by `|`). `false` hides it. */
  toolbar?: string | false;
  /** Plugin names to load. Only "lists", "link" and "autoresize" are bundled/imported above --
   *  pass others only if you've also imported them, or TinyMCE will silently skip them. */
  plugins?: string[];
  menubar?: boolean | string;
  statusbar?: boolean;

  /** [hex, label, hex, label, ...] pairs shown in the forecolor/backcolor pickers. */
  colorMap?: string[];
  /** Appended after the default body styles (fonts, readonly background, link underline). */
  contentStyle?: string;
  /** TinyMCE's allowed-tags/attributes schema. Keep restrictive for plain-text-ish fields. */
  validElements?: string;
  /** Strip formatting from pasted content instead of keeping it. */
  pasteAsText?: boolean;

  className?: string;

  /** Escape hatch: merged on top of everything above for one-off TinyMCE `init` overrides. */
  init?: Partial<RawEditorOptions>;
};

const TinyEditor: React.FC<TinyEditorProps> = ({
  value,
  onChange,
  placeholder = "Start typing...",
  disabled = false,
  height = 300,
  minHeight = 300,
  maxHeight = 500,
  toolbar = DEFAULT_TOOLBAR,
  plugins = DEFAULT_PLUGINS,
  menubar = false,
  statusbar = false,
  colorMap = DEFAULT_COLOR_MAP,
  contentStyle = "",
  validElements = DEFAULT_VALID_ELEMENTS,
  pasteAsText = false,
  className,
  init,
}) => {
  const useAutoresize = plugins.includes("autoresize");

  // Suppresses onEditorChange calls triggered by our own `value` prop updates, so programmatic
  // changes aren't reported back as user edits. Armed synchronously during render, before
  // tinymce-react's own componentDidUpdate runs.
  const lastValueRef = useRef(value);
  const suppressChangeRef = useRef(false);
  if (value !== lastValueRef.current) {
    lastValueRef.current = value;
    suppressChangeRef.current = true;
  }
  useEffect(() => {
    suppressChangeRef.current = false;
  }, [value]);

  return (
    <div className={className}>
      <style>{`
        .tox-tinymce { border-radius: 8px; border-color: #d8dbe6; overflow: hidden; transition: border-color .15s ease, box-shadow .15s ease; }
        .tox-tinymce:focus-within { border-color: #182b49; box-shadow: 0 0 0 0.2rem rgba(24, 43, 73, 0.15); }
        .tox .tox-toolbar,
        .tox .tox-toolbar__overflow,
        .tox .tox-toolbar__primary { background: #f8f9fb; }
      `}</style>
      <TinyMCEReactEditor
        // Controlled: tinymce-react calls setContent() only when `value` actually changes, and
        // preserves the cursor position when it does. The `BeforeSetContent` hook below guards
        // this (and any other) incoming content before TinyMCE's parser ever sees it.
        value={value}
        // Only report genuine user edits; programmatic value changes are filtered via
        // suppressChangeRef above. `content` already arrives unguarded -- the `GetContent` hook
        // below runs first, inside every editor.getContent() call this relies on.
        onEditorChange={(content) => {
          if (suppressChangeRef.current) return;
          onChange?.(content);
        }}
        disabled={disabled}
        licenseKey="gpl"
        init={{
          height,
          menubar,
          statusbar,
          plugins,
          toolbar,
          color_map: colorMap,
          line_height_formats: "1 1.15 1.5 2 3",
          placeholder,
          branding: false,
          content_style: DEFAULT_CONTENT_STYLE + contentStyle,

          base_url: "/tinymce", // path to where TinyMCE is served
          suffix: ".min",

          // Autoresize config, only relevant while the "autoresize" plugin is loaded.
          ...(useAutoresize
            ? {
                autoresize_bottom_margin: 50,
                autoresize_min_height: minHeight,
                autoresize_max_height: maxHeight,
              }
            : {}),

          newline_behavior: "linebreak", // Enter inserts <br>
          forced_root_block: false, // don't wrap content in <p>
          valid_elements: validElements,
          paste_as_text: pasteAsText,

          convert_urls: false, // don't rewrite URLs
          encode_entities: false, // don't HTML-encode entities
          entity_encoding: "raw", // keep what you type as-is

          ...init,

          setup: (editor) => {
            // Single guard point: every path that inserts content (the `value` prop via
            // tinymce-react's internal setContent, paste, and the code-view dialog's Save) routes
            // through BeforeSetContent, so this is the only place we need to rename the property
            // before TinyMCE's parser can see -- and lowercase -- a literal "color"/
            // "background-color". Left guarded internally; unguarded once on the way out in
            // onEditorChange above.
            editor.on("BeforeSetContent", (e) => {
              e.content = guardColorPlaceholders(e.content);
            });
            // Mirror image of the above: every editor.getContent() call (onEditorChange, the
            // code-view dialog re-reading current content to display it, etc.) routes through
            // here, so this is the single place the property gets its real name back.
            editor.on("GetContent", (e) => {
              e.content = unguardColorPlaceholders(e.content);
            });
            init?.setup?.(editor);
          },
        }}
      />
    </div>
  );
};

export default TinyEditor;
